import { useEffect, useState } from 'react';
import './astyles/DonationsList.css';
import { exportToCSV, exportToPDF, formatDataForExport } from '../../utils/exportUtils';
import { Download, FileText, Filter } from 'lucide-react';

interface Donation {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  amount: number;
  donation_type: string;
  payment_token: string;
  transaction_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

function DonationsList() {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [filteredDonations, setFilteredDonations] = useState<Donation[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/donations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setDonations(data);
      setFilteredDonations(data);
    } catch (err) {
      console.error('Error fetching donations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch donations');
    } finally {
      setLoading(false);
    }
  };

  // Filter donations based on search term, type, and status
  useEffect(() => {
    let filtered = donations;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(donation => {
        const name = `${donation.first_name || ''} ${donation.last_name || ''}`.toLowerCase();
        const email = donation.email?.toLowerCase() || '';
        const transactionId = donation.transaction_id?.toLowerCase() || '';
        
        return name.includes(term) || email.includes(term) || transactionId.includes(term);
      });
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(donation => 
        donation.donation_type === typeFilter
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(donation => 
        (donation.status || 'complete') === statusFilter
      );
    }

    setFilteredDonations(filtered);
  }, [searchTerm, typeFilter, statusFilter, donations]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchDonations();
  };

  // Export functions
  const handleExportCSV = () => {
    const formattedData = formatDataForExport(filteredDonations, 'donations');
    exportToCSV(formattedData, `donations_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const formattedData = formatDataForExport(filteredDonations, 'donations');
      await exportToPDF(formattedData, `donations_${new Date().toISOString().split('T')[0]}`, 'Donations List');
    } finally {
      setExportLoading(false);
    }
  };

  // Get unique values for filters
  const getUniqueTypes = () => {
    const types = donations.map(d => d.donation_type || 'unknown');
    return Array.from(new Set(types)).sort();
  };

  const getUniqueStatuses = () => {
    const statuses = donations.map(d => d.status || 'complete');
    return Array.from(new Set(statuses)).sort();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading donations...</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Donations</h3>
        <p>Failed to load donation data: {error}</p>
        <button onClick={handleRetry} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="donations-list-container">
      <div className="donations-header">
        <h2>Donations Management</h2>
        <div className="donations-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name, email, or transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="filter-container">
            <Filter size={16} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="type-filter"
            >
              <option value="all">All Types</option>
              {getUniqueTypes().map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Statuses</option>
              {getUniqueStatuses().map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="export-buttons">
            <button
              onClick={handleExportCSV}
              className="export-btn csv-btn"
              disabled={filteredDonations.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="export-btn pdf-btn"
              disabled={filteredDonations.length === 0 || exportLoading}
            >
              <FileText size={16} />
              {exportLoading ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="donations-table-wrapper">
        <table className="donations-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Donor Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Transaction ID</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredDonations.length === 0 ? (
              <tr>
                <td colSpan={9} className="no-data">
                  No donations found
                </td>
              </tr>
            ) : (
              filteredDonations.map(donation => (
                <tr key={donation.id}>
                  <td>{donation.id}</td>
                  <td>{donation.first_name} {donation.last_name}</td>
                  <td>{donation.email}</td>
                  <td>{donation.phone || '-'}</td>
                  <td className="amount-cell">{formatCurrency(donation.amount)}</td>
                  <td>
                    <span className={`donation-type-badge ${donation.donation_type}`}>
                      {donation.donation_type}
                    </span>
                  </td>
                  <td className="transaction-id">{donation.transaction_id || '-'}</td>
                  <td>
                    <span className={`status-badge ${donation.status}`}>
                      {donation.status || 'complete'}
                    </span>
                  </td>
                  <td>{new Date(donation.created_at).toLocaleDateString('en-ZA')}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="donations-footer">
        <p>
          Showing {filteredDonations.length} of {donations.length} donations
          {filteredDonations.length !== donations.length && ' (filtered)'}
        </p>
        <p className="total-amount">
          Total Amount: {formatCurrency(donations.reduce((sum, d) => sum + d.amount, 0))}
        </p>
      </div>
    </div>
  );
}

export default DonationsList;

